// Who gets the 5-point canvasser carve-out on a deal: the one place every money
// surface reads it from (commission board, My Commissions, the home band,
// /financials, job analysis), so they cannot drift apart.
//
// The rule (Greg, Oct 19, 2026; the SQL side is
// canvasser_recognize_captured_revenue() in migration 20261019d):
//   * deals.canvass_source records who brought the lead in:
//       'canvas_app'   an approved canvas-tool appointment from a canvasser
//       'lead_source'  the lead source names a canvasser, active or not
//       'canvass_team' the lead source is "Canvass Team" (nobody to credit)
//   * a canvas-tool appointment carries the carve-out on every sale;
//   * the other two carry it only once an RM revived the lead off the Call List
//     (deals.recanvass_set_at);
//   * an RM's own lead never carries it, however it was booked.

import { supabase } from "./supabase";

/** The canvasser a carve-out is credited to. id is null for "Canvass Team". */
export type CanvasserRef = { id: string | null; name: string };

type CanvassRow = {
  id: string;
  canvass_source: "canvas_app" | "lead_source" | "canvass_team" | null;
  source_canvasser_id: string | null;
  recanvass_set_at: string | null;
  canvasser: { full_name: string | null } | { full_name: string | null }[] | null;
};

/** True when a deal carries the carve-out under the rule above. */
export function carveOutApplies(row: Pick<CanvassRow, "canvass_source" | "recanvass_set_at">): boolean {
  if (row.canvass_source === "canvas_app") return true;
  return !!row.canvass_source && !!row.recanvass_set_at;
}

/** deal id → the canvasser the carve-out is credited to, for the deals that
 *  carry one. Throws on a failed read: a money board that silently drops the
 *  carve-out would overpay the RM. */
export async function fetchCanvasserByDeal(dealIds: string[]): Promise<Record<string, CanvasserRef>> {
  const out: Record<string, CanvasserRef> = {};
  const ids = [...new Set(dealIds.filter(Boolean))];
  // .in() goes in the URL; chunk to keep it under length limits.
  for (let i = 0; i < ids.length; i += 200) {
    const chunk = ids.slice(i, i + 200);
    const { data, error } = await supabase
      .from("deals")
      // Explicit FK name: deals has several FKs to team_roster.
      .select("id, canvass_source, source_canvasser_id, recanvass_set_at, canvasser:team_roster!deals_source_canvasser_id_fkey(full_name)")
      .in("id", chunk)
      .not("canvass_source", "is", null);
    if (error) throw error;
    for (const r of (data ?? []) as CanvassRow[]) {
      if (!carveOutApplies(r)) continue;
      const c = Array.isArray(r.canvasser) ? r.canvasser[0] : r.canvasser;
      out[r.id] = {
        id: r.source_canvasser_id,
        name: r.canvass_source === "canvass_team" ? "Canvass Team" : c?.full_name ?? "Canvasser",
      };
    }
  }
  return out;
}
